import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { LayoutService } from '../../layouts/layout.service';
import { FilterBarComponent, FilterState } from '../../shared/components/filter-bar/filter-bar';
import { DataTableComponent, ColumnDef } from '../../shared/components/data-table/data-table';
import { SelectOption } from '../../shared/components/select/select';
import { InvestigationListItem as Investigation } from '../../shared/models/investigation.models';
import { InvestigationService } from '../../core/services/investigation.service';
import { ProgramService } from '../../core/services/program.service';
import { CategoryService } from '../../core/services/category.service';
import { NewInvestigationModalComponent } from '../../shared/components/new-investigation-modal/new-investigation-modal';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector: 'app-investigation-feed',
  imports: [FilterBarComponent, DataTableComponent, NewInvestigationModalComponent],
  templateUrl: './investigation-feed.html',
})
export class InvestigationFeedComponent implements OnInit {
  private layout = inject(LayoutService);
  private router = inject(Router);
  private investigationService = inject(InvestigationService);
  private programService = inject(ProgramService);
  private categoryService = inject(CategoryService);
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  private categoriesTpl =
    viewChild.required<TemplateRef<{ $implicit: Investigation }>>('categoriesTpl');
  private personsTpl = viewChild.required<TemplateRef<{ $implicit: Investigation }>>('personsTpl');
  private linkedIncidentTpl =
    viewChild.required<TemplateRef<{ $implicit: Investigation }>>('linkedIncidentTpl');
  private dateTpl = viewChild.required<TemplateRef<{ $implicit: Investigation }>>('dateTpl');

  protected readonly canCreate = this.auth.canDo('Investigations', ['Creation']);
  protected showInvestigationModal = signal(false);
  protected loading = signal(false);
  protected investigations = signal<Investigation[]>([]);
  protected total = signal(0);
  protected page = signal(1);
  protected readonly pageSize = 10;
  protected programOptions = signal<SelectOption[]>([]);
  protected categoryOptions = signal<SelectOption[]>([]);

  protected columns = computed((): ColumnDef<Investigation>[] => [
    {
      key: 'investigationNumber',
      header: 'ID',
      cell: (i) => i.investigationNumber ?? i.id,
      cellClass: 'font-bold text-navy-900 whitespace-nowrap',
    },
    {
      key: 'programName',
      header: 'Program',
      cell: (i) => i.programName,
      cellClass: 'font-semibold text-navy-800 whitespace-nowrap',
    },
    { key: 'linkedIncident', header: 'Linked Incident', cellTemplate: this.linkedIncidentTpl() },
    { key: 'categories', header: 'Category', cellTemplate: this.categoriesTpl() },
    { key: 'persons', header: 'Person Involved', cellTemplate: this.personsTpl() },
    { key: 'date', header: 'Date', cellTemplate: this.dateTpl() },
  ]);

  protected readonly statusOptions: SelectOption[] = [
    { value: '', label: 'All' },
    { value: 'Open', label: 'Open' },
    { value: 'Closed', label: 'Closed' },
  ];

  private filter$ = new Subject<FilterState>();
  private load$ = new Subject<void>();
  private lastFilter: FilterState = {
    search: '',
    programId: '',
    categoryId: '',
    dateFrom: '',
    dateTo: '',
    status: '',
  };

  ngOnInit(): void {
    this.layout.set({
      title: 'Investigations Feed',
      breadcrumb: 'Advanced forensic oversight for clinical and operational case reviews.',
      showBackButton: false,
      switchPlaces: false,
      darkHeader: false,
      headerAction: this.canCreate
        ? {
            label: 'Create Investigation',
            icon: 'plus',
            onClick: () => this.showInvestigationModal.set(true),
          }
        : undefined,
    });

    this.loadOptions();

    // Single request pipeline: filters, paging and refreshes all go through load$.
    this.load$
      .pipe(
        switchMap(() => {
          const f = this.lastFilter;
          this.loading.set(true);
          return this.investigationService.getInvestigations({
            filter: f.search,
            programId: f.programId || undefined,
            categoryId: f.categoryId || undefined,
            dateFrom: f.dateFrom || undefined,
            dateTo: f.dateTo || undefined,
            status: f.status || undefined,
            pageSize: this.pageSize,
            pageIndex: this.page() - 1,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.investigations.set(res.investigations ?? []);
          this.total.set(res.total);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });

    this.filter$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (a, b) =>
            a.search === b.search &&
            a.programId === b.programId &&
            a.categoryId === b.categoryId &&
            a.dateFrom === b.dateFrom &&
            a.dateTo === b.dateTo &&
            a.status === b.status,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((f) => {
        this.lastFilter = f;
        this.page.set(1);
        this.load$.next();
      });

    this.load$.next();
  }

  private loadOptions(): void {
    this.programService
      .getPrograms()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) =>
          this.programOptions.set([
            { value: '', label: 'All' },
            ...res.programs.map((p) => ({ value: p.id, label: p.name })),
          ]),
      });

    this.categoryService
      .getSituationCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) =>
          this.categoryOptions.set([
            { value: '', label: 'All' },
            ...res.situationCategories.map((c) => ({ value: c.id, label: c.name })),
          ]),
      });
  }

  protected onFiltersChange(filters: FilterState): void {
    this.filter$.next(filters);
  }

  protected onPageChange(page: number): void {
    this.page.set(page);
    this.load$.next();
  }

  protected onInvestigationCreated(): void {
    this.showInvestigationModal.set(false);
    this.page.set(1);
    this.load$.next();
  }

  protected onRowAction(investigation: Investigation): void {
    this.router.navigate(['/investigations', investigation.id]);
  }

  protected formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${date} • ${time}`;
  }
}
